import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface CourseRow {
  readonly courseId: string;
  readonly courseCode: string;
  readonly courseName: string;
  readonly contentType: string;
  readonly isCompliance: boolean;
  readonly status: string;
}
interface PathRow {
  readonly learningPathId: string;
  readonly pathCode: string;
  readonly pathName: string;
  readonly pathType: string;
  readonly status: string;
}
interface AssessmentRow {
  readonly assessmentId: string;
  readonly assessmentCode: string;
  readonly assessmentName: string;
  readonly assessmentType: string;
  readonly status: string;
}

/**
 * M35 — Learning Management Dashboard (§33). Read-only overview of the §10
 * Learning Management catalogue — courses (T469), learning paths (T471) and
 * assessments (T472) — surfacing the catalogue totals, the compliance-course
 * count and a preview of the most recent items in each corpus. Tenant
 * isolation is enforced server-side (LAW 4).
 */
export function LearningManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [paths, setPaths] = useState<PathRow[]>([]);
  const [assessments, setAssessments] = useState<AssessmentRow[]>([]);
  const [courseTotal, setCourseTotal] = useState(0);
  const [pathTotal, setPathTotal] = useState(0);
  const [assessmentTotal, setAssessmentTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: CourseRow[]; total: number }>(`/v1/kms/courses?limit=5`, { token }),
      apiRequest<{ data: PathRow[]; total: number }>(`/v1/kms/learning-paths?limit=5`, { token }),
      apiRequest<{ data: AssessmentRow[]; total: number }>(`/v1/kms/assessments?limit=5`, { token }),
    ])
      .then(([c, p, a]) => {
        if (!alive) return;
        setCourses(c.data); setCourseTotal(c.total);
        setPaths(p.data); setPathTotal(p.total);
        setAssessments(a.data); setAssessmentTotal(a.total);
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const complianceCourses = courses.filter((c) => c.isCompliance).length;

  return (
    <main aria-label="lms-dashboard">
      <h1>{t('m35.lms.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <section aria-label="lms-totals" className="kpi-grid">
            <div className="kpi-card"><span className="kpi-label">{t('m35.lms.courses')}</span><span className="kpi-value" aria-label="total-courses">{courseTotal}</span></div>
            <div className="kpi-card"><span className="kpi-label">{t('m35.lms.paths')}</span><span className="kpi-value" aria-label="total-paths">{pathTotal}</span></div>
            <div className="kpi-card"><span className="kpi-label">{t('m35.lms.assessments')}</span><span className="kpi-value" aria-label="total-assessments">{assessmentTotal}</span></div>
            <div className="kpi-card"><span className="kpi-label">{t('m35.lms.complianceCourses')}</span><span className="kpi-value" aria-label="compliance-courses">{complianceCourses}</span></div>
          </section>

          <DataTable<CourseRow>
            ariaLabel="course-preview-table"
            testId="course-preview-table"
            caption={t('m35.lms.recentCourses')}
            rowKey={(r) => r.courseId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={courses}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.courseCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.courseName },
              { key: 'content', header: t('m35.lms.content'), render: (r) => r.contentType },
              { key: 'compliance', header: t('m35.lms.compliance'), render: (r) => r.isCompliance ? t('m35.common.yes') : t('m35.common.no') },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
            ]}
          />

          <DataTable<PathRow>
            ariaLabel="path-preview-table"
            testId="path-preview-table"
            caption={t('m35.lms.recentPaths')}
            rowKey={(r) => r.learningPathId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={paths}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.pathCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.pathName },
              { key: 'type', header: t('m35.common.type'), render: (r) => r.pathType },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
            ]}
          />

          <DataTable<AssessmentRow>
            ariaLabel="assessment-preview-table"
            testId="assessment-preview-table"
            caption={t('m35.lms.recentAssessments')}
            rowKey={(r) => r.assessmentId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={assessments}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.assessmentCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.assessmentName },
              { key: 'type', header: t('m35.common.type'), render: (r) => r.assessmentType },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
            ]}
          />
        </>
      )}
    </main>
  );
}
